import tw from 'tailwind-styled-components'
import {cn} from '@containers/html-utils'
import {Color, Size, State} from './types'

const primaryMixin = (forcedState?: State, disabled?: boolean): string => {
  if (disabled) {
    return 'text-action-disabled'
  }

  const cx: string[] = ['cursor-pointer']
  if (forcedState === 'active') {
    cx.push('text-action-pressed')
  } else if (forcedState === 'hover') {
    cx.push('text-action-hover')
  } else if (forcedState === 'default') {
    cx.push('text-action-default')
  } else {
    cx.push(
      'text-action-default',
      'hover:text-action-hover',
      'active:text-action-pressed'
    )
  }
  return cx.join(' ')
}

const secondaryMixin = (forcedState?: State, disabled?: boolean): string => {
  if (disabled) {
    return 'text-action-disabled'
  }

  const cx: string[] = ['cursor-pointer']
  if (forcedState === 'active') {
    cx.push('text-placeholder-new')
  } else if (forcedState === 'hover') {
    cx.push('text-primary')
  } else if (forcedState === 'default') {
    cx.push('text-secondary')
  } else {
    cx.push(
      'text-secondary',
      'hover:text-primary',
      'active:text-placeholder-new'
    )
  }
  return cx.join(' ')
}

const notificationPrimaryMixin = (
  forcedState?: State,
  disabled?: boolean
): string => {
  if (disabled) {
    return 'text-action-disabled'
  }

  const cx: string[] = ['cursor-pointer']
  if (forcedState === 'active') {
    cx.push('text-notification-primary')
  } else if (forcedState === 'hover') {
    cx.push('text-notification-primary')
  } else if (forcedState === 'default') {
    cx.push('text-notification-primary')
  } else {
    cx.push(
      'text-notification-primary',
      'hover:text-notification-primary',
      'active:text-notification-primary'
    )
  }
  return cx.join(' ')
}

const iconPrimaryMixin = (forcedState?: State, disabled?: boolean): string => {
  const cx: string[] = []
  if (disabled) {
    cx.push('text-icon-disabled')
  } else {
    cx.push('cursor-pointer')

    if (forcedState === 'default') {
      cx.push('text-icon-secondary')
    } else if (forcedState === 'hover') {
      cx.push('text-action-hover')
    } else if (forcedState === 'active') {
      cx.push('text-action-pressed')
    } else {
      cx.push(
        'text-icon-secondary',
        'hover:text-action-hover',
        'active:text-action-pressed'
      )
    }
  }
  return cx.join(' ')
}

const viewTabMixin = (forcedState?: State, disabled?: boolean): string => {
  const cx: string[] = []
  if (disabled) {
    cx.push('text-icon-disabled')
  } else {
    cx.push('cursor-pointer')

    if (forcedState === 'default') {
      cx.push('text-secondary')
    } else if (forcedState === 'hover') {
      cx.push('text-secondary')
    } else if (forcedState === 'active') {
      cx.push('text-action-default')
    } else {
      cx.push(
        'text-icon-secondary',
        'hover:text-secondary',
        'active:text-action-default'
      )
    }
  }
  return cx.join(' ')
}

const colorMixin = (p: {
  $color: Color
  $forcedState?: State
  $disabled?: boolean
}): string => {
  switch (p.$color) {
    case 'primary':
      return primaryMixin(p.$forcedState, p.$disabled)
    case 'secondary':
      return secondaryMixin(p.$forcedState, p.$disabled)
    case 'notification-primary':
      return notificationPrimaryMixin(p.$forcedState, p.$disabled)
    case 'icon-primary':
      return iconPrimaryMixin(p.$forcedState, p.$disabled)
    case 'view/tab':
      return viewTabMixin(p.$forcedState, p.$disabled)
    default:
      throw new Error(`Unsupported color '${p.$color}'`)
  }
}

const bgViewTabMixin = (forcedState?: State, disabled?: boolean): string => {
  if (disabled) {
    return ''
  }

  const cx: string[] = []
  if (forcedState === 'active') {
    cx.push('bg-background-primary shadow-sm')
  } else if (forcedState === 'hover') {
    cx.push('bg-background-hover')
  } else {
    cx.push(
      'hover:bg-background-hover',
      'active:bg-background-primary',
      'active:shadow-sm'
    )
  }
  return cx.join(' ')
}

const bgMixin = (p: {
  $color: Color
  $forcedState?: State
  $disabled?: boolean
}): string => {
  switch (p.$color) {
    case 'view/tab':
      return bgViewTabMixin(p.$forcedState, p.$disabled)
    default:
      return ''
  }
}

const textMixin = (size: Size): string => {
  const cx: string[] = []
  switch (size) {
    case 'md':
      cx.push('leading-4', 'text-14')
      break
    case 'sm':
      cx.push('leading-4', 'text-12')
      break
    case 'xs':
      cx.push('text-xs-regular')
      break
  }
  return cx.join(' ')
}

const sizeMixin = (color: Color): string => {
  switch (color) {
    case 'view/tab':
      return 'py-4 pl-4 pr-6 rounded-4'
    default:
      return ''
  }
}

export const TwLinkButtonNew = tw.div<{
  $color: Color
  $size: Size
  $disabled?: boolean
  $forcedState?: State
}>`
  select-none
  w-fit
  text-sm-regular
  flex
  gap-4
  items-center
  justify-center
  ${p => cn(colorMixin(p), textMixin(p.$size), bgMixin(p), sizeMixin(p.$color))}
`

export const TwText = tw.span`
  whitespace-nowrap
  overflow-hidden
  text-ellipsis
`
